import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import { formatRoute } from 'react-router-named-routes';
import { SMORG_STUDIO_MEMBERSHIP_TIER_ROUTE } from '../../services/routes';
import MembershipTierDetailModal from './membership_tier_detail_modal';
import MembershipTiersBoard from './membership_tiers_board';
import {
  useDeleteMembershipTierMutation,
  useGetMembershipTiersBySpaceQuery,
  useGetSpaceQuery,
  useUpdateMembershipTierMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { membershipTiersAfterMembershipTierMoved } from '../../services/membership_tiers';

const MembershipTiersScreen = () => {
  const navigate = useNavigate();

  const { data: space } = useGetSpaceQuery();

  const { data: membershipTiers } = useGetMembershipTiersBySpaceQuery(
    space?.id,
    {
      skip: !space?.id,
    },
  );

  const [updateMembershipTier] = useUpdateMembershipTierMutation();

  const [deleteMembershipTier] = useDeleteMembershipTierMutation();

  const onCardClick = (membershipTierID) => {
    navigate(
      formatRoute(SMORG_STUDIO_MEMBERSHIP_TIER_ROUTE, { membershipTierID }),
    );
  };

  const afterCardDrag = async (
    membershipTierID,
    _sourceLaneId,
    _targetLaneId,
    position,
  ) => {
    const targetMembershipTier = membershipTiers.find(
      (mt) => mt.id === membershipTierID,
    );
    if (!targetMembershipTier) {
      return false;
    }
    const membershipTierState = targetMembershipTier.state;
    const updatedMembershipTiers = membershipTiersAfterMembershipTierMoved(
      membershipTiers,
      membershipTierID,
      position,
    );
    // Rank has been updated potentially on all membership tiers in this state, all need to be sync'ed
    const allMembershipTiersAffected = updatedMembershipTiers.filter(
      (mt) => mt.state === membershipTierState,
    );
    await Promise.all(
      allMembershipTiersAffected.map((mt) => updateMembershipTier(mt)),
    );
    // Cancel the drop, because the board will re-render with the new structure.
    return false;
  };

  const onCardDelete = async (membershipTierID) => {
    await deleteMembershipTier(membershipTierID);
  };

  const { membershipTierID } = useParams();

  const detailVisibleFor = useMemo(() => {
    const dvf = {
      membershipTierID: null,
    };
    if (membershipTierID) {
      dvf.membershipTierID = membershipTierID;
    }
    return dvf;
  }, [membershipTierID]);

  return (
    <>
      <Grid container style={{ flexFlow: 'row' }}>
        <Grid item xs={12} style={{ minWidth: '400px', overflow: 'hidden' }}>
          <MembershipTiersBoard
            membershipTiers={membershipTiers}
            onCardClick={useCallback(onCardClick, [navigate])}
            onCardDelete={useCallback(onCardDelete, [deleteMembershipTier])}
            handleDragEnd={useCallback(afterCardDrag, [
              membershipTiers,
              updateMembershipTier,
            ])}
          />
        </Grid>
      </Grid>
      {detailVisibleFor.membershipTierID && (
        <MembershipTierDetailModal
          membershipTierID={detailVisibleFor.membershipTierID}
          onDismiss={() => navigate(-1)}
        />
      )}
    </>
  );
};

export default MembershipTiersScreen;
